import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";
import { Editor, EditorStateSnapshot, useTiptapState } from "@tiptap/react";

import {
    FormatBoldIcon,
    FormatItalicIcon,
    FormatUnderlinedIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";

import { LinkEditor } from "./components";
import { EditorState, ToolbarInnerProps } from "./types";

const ToolbarInner = ({ editor, isDisabled }: ToolbarInnerProps) => {
    const { t } = useTranslation();

    const editorState: EditorState = useTiptapState(
        (ctx: EditorStateSnapshot<Editor>) => ({
            isBold: ctx.editor.isActive("bold"),
            canBold: ctx.editor.can().toggleBold(),
            isItalic: ctx.editor.isActive("italic"),
            canItalic: ctx.editor.can().toggleItalic(),
            isUnderline: ctx.editor.isActive("underline"),
            canUnderline: ctx.editor.can().toggleUnderline(),
            isLink: ctx.editor.isActive("link"),
            canLink: ctx.editor.can().toggleLink()
        })
    );

    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
                columnGap: "8px",

                "@media (max-width: 599px)": {
                    flexWrap: "wrap"
                }
            })}
        >
            <TooltipGeneral
                placement={TOOLTIP_PLACEMENT.Top}
                title={t("Banner##bold")}
            >
                <span>
                    <Button
                        color={
                            editorState.isBold
                                ? BUTTON_COLORS.Primary
                                : BUTTON_COLORS.White
                        }
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={<FormatBoldIcon />}
                        disabled={isDisabled ?? !editorState.canBold}
                        onClick={() =>
                            editor.chain().focus().toggleBold().run()
                        }
                    />
                </span>
            </TooltipGeneral>

            <TooltipGeneral
                placement={TOOLTIP_PLACEMENT.Top}
                title={t("Banner##italic")}
            >
                <span>
                    <Button
                        color={
                            editorState.isItalic
                                ? BUTTON_COLORS.Primary
                                : BUTTON_COLORS.White
                        }
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={<FormatItalicIcon />}
                        disabled={isDisabled ?? !editorState.canItalic}
                        onClick={() =>
                            editor.chain().focus().toggleItalic().run()
                        }
                    />
                </span>
            </TooltipGeneral>

            <TooltipGeneral
                placement={TOOLTIP_PLACEMENT.Top}
                title={t("Banner##underline")}
            >
                <span>
                    <Button
                        color={
                            editorState.isUnderline
                                ? BUTTON_COLORS.Primary
                                : BUTTON_COLORS.White
                        }
                        size={BUTTON_SIZES.Small}
                        variant={BUTTON_VARIANTS.IconOnly}
                        icon={<FormatUnderlinedIcon />}
                        disabled={isDisabled ?? !editorState.canUnderline}
                        onClick={() =>
                            editor.chain().focus().toggleUnderline().run()
                        }
                    />
                </span>
            </TooltipGeneral>

            <LinkEditor
                editor={editor}
                editorState={editorState}
                isDisabled={isDisabled}
            />
        </div>
    );
};

export default ToolbarInner;
